import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { AppOverlay } from '@/components/AppOverlay';
import { forceRefreshTokens } from '@/components/AuthProvider';
import { ErrorBoundary as ErrorBoundaryImpl } from '@/components/ErrorBoundaries/ErrorBoundary';
import { ErrorBoundaryWithReset } from '@/components/ErrorBoundaries/ErrorBoundaryWithReset';
import { LazyLoadingFallback } from '@/components/Loaders/LazyLoadingFallback';
import { OverlayWidthProvider } from '@/components/Preview/hooks/useOverlayWidthDetails';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { TrackRouteChange } from '@/components/TrackRouteChange';
import { NavContextProvider } from '@/context/NavContext';
import { AskNeedlContextProvider } from '@/features/ChatBotV2/context';
import { useFetchUserPreferences } from '@/hooks/query/useFetchUserPreferences';
import {
  ASSET_REDIRECT_ROUTE,
  AUTH_ROUTES,
  DOCUMENT_ROUTE,
  IMAGE_ROUTE,
  NEEDL_PULSE_ROUTES,
  OAUTH_ROUTES,
  ONBOARDING_ROUTES,
  REDIRECT_BASE_ROUTE,
} from '@/nav-routes';
import { useNavigate } from '@/router';

const cloudProvider = import.meta.env.VITE_CLOUD_PROVIDER;
export const OAuthCallbackHandlerV2 = React.lazy(
  () => import('./OAuthCallbackV2')
);
const Onboarding = React.lazy(() => import('./Onboarding'));
const ImageViewer = React.lazy(() => import('./ImageViewer'));
const PDFViewer = React.lazy(() => import('./PDFViewer'));
const AuthRoutesV2 = React.lazy(() => import('./AuthRoutesV2'));
const ConditionalRoot = React.lazy(() => import('./ConditionalRoot'));
const NeedlPulseRoutes = React.lazy(() => import('@/features/NeedlPulse'));
const Redirect = React.lazy(() => import('./Redirect'));
const AssetRedirect = React.lazy(() => import('@/components/AssetRedirect'));

const AppStateWrapper: React.FunctionComponent = ({ children }) => {
  const homeFeedMatch = useRouteMatch('/feeds/home');
  const firstRender = React.useRef(true);

  const isHomeFeed = !!homeFeedMatch;

  React.useEffect(() => {
    if (isHomeFeed && firstRender.current) {
      trackEvent('everything_feed', { homeFeed: true });
      firstRender.current = false;
    }
  }, [isHomeFeed]);

  React.useEffect(() => {
    let interval: NodeJS.Timeout | null;

    const refreshSession = async () => {
      const refresh = async () => {
        await forceRefreshTokens();
      };

      const refreshInterval =
        cloudProvider === 'aws' ? 4 * 60 * 1000 : 30 * 60 * 1000;

      if (document.visibilityState === 'visible') {
        await refresh();

        interval = setInterval(async () => {
          await refresh();
        }, refreshInterval);
      } else {
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };

    window.addEventListener('visibilitychange', refreshSession);

    return () => {
      window.removeEventListener('visibilitychange', refreshSession);
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
  }, []);

  return (
    <>
      {children}
      <TrackRouteChange />
    </>
  );
};

const ContextProviders: React.FunctionComponent = ({ children }) => {
  const { isLoading, data: userPreferences } = useFetchUserPreferences();

  if (isLoading) {
    return <LazyLoadingFallback />;
  }

  return (
    <NavContextProvider initialState={userPreferences?.navState}>
      <AskNeedlContextProvider>
        <OverlayWidthProvider>{children}</OverlayWidthProvider>
      </AskNeedlContextProvider>
    </NavContextProvider>
  );
};

const RouteWithProvider = () => {
  return (
    <ContextProviders>
      <Switch>
        <ProtectedRoute path={IMAGE_ROUTE}>
          <ErrorBoundary
            FallbackComponent={ErrorBoundaryWithReset}
            onReset={() => history.go(-1)}
          >
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <ImageViewer />
            </React.Suspense>
          </ErrorBoundary>
        </ProtectedRoute>
        <ProtectedRoute path='/'>
          <ErrorBoundary FallbackComponent={ErrorBoundaryImpl}>
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <ConditionalRoot />
            </React.Suspense>
          </ErrorBoundary>
        </ProtectedRoute>
      </Switch>
      <AppOverlay />
    </ContextProviders>
  );
};

const App = (): JSX.Element => {
  const navigate = useNavigate();

  return (
    <AppStateWrapper>
      <Switch>
        {/* Needl Pulse Routes */}
        <Route path={NEEDL_PULSE_ROUTES.BASE_ROUTE}>
          <ErrorBoundary FallbackComponent={ErrorBoundaryImpl}>
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <NeedlPulseRoutes />
            </React.Suspense>
          </ErrorBoundary>
        </Route>

        {/* Needl Routes */}
        <Route path={[OAUTH_ROUTES.REDIRECT, AUTH_ROUTES.BASE_ROUTE]}>
          <ErrorBoundary
            FallbackComponent={ErrorBoundaryWithReset}
            onReset={() => history.go(-1)}
          >
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <AuthRoutesV2 />
            </React.Suspense>
          </ErrorBoundary>
        </Route>
        <Route path={DOCUMENT_ROUTE}>
          <ErrorBoundary
            FallbackComponent={ErrorBoundaryWithReset}
            onReset={() => history.go(-1)}
          >
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <PDFViewer />
            </React.Suspense>
          </ErrorBoundary>
        </Route>
        <ProtectedRoute
          path={ASSET_REDIRECT_ROUTE}
          shouldPrefetchInitialData={false}
        >
          <ErrorBoundary
            FallbackComponent={ErrorBoundaryWithReset}
            onReset={() => history.go(-1)}
          >
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <AssetRedirect />
            </React.Suspense>
          </ErrorBoundary>
        </ProtectedRoute>
        <ProtectedRoute path={REDIRECT_BASE_ROUTE}>
          <ErrorBoundary
            FallbackComponent={ErrorBoundaryWithReset}
            onReset={() => history.go(-1)}
          >
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <Redirect />
            </React.Suspense>
          </ErrorBoundary>
        </ProtectedRoute>
        <ProtectedRoute path={ONBOARDING_ROUTES.BASE_ROUTE}>
          <ErrorBoundary
            FallbackComponent={ErrorBoundaryWithReset}
            onReset={navigate.goBack}
          >
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <Onboarding />
            </React.Suspense>
          </ErrorBoundary>
        </ProtectedRoute>
        <ProtectedRoute path='*'>
          <RouteWithProvider />
        </ProtectedRoute>
      </Switch>
    </AppStateWrapper>
  );
};

export default App;
